/**
 * ชนิดข้อมูลของ "ข้อยกเว้นการรับของ" (Receiving Exceptions) — คิวที่จัดซื้อทำงาน
 * และรายงานสรุป ตาม payload contract ที่ freeze แล้ว (D-168 B6, D-091, D-168 A6)
 *
 * `GET /v1/receiving-exceptions` อ่านแถวจากตารางเดียวกับ `ReceivingDiscrepancy`
 * (D-091) แต่กรองเหลือสามชนิดที่เป็นคิวงานของจัดซื้อ (`SHORT`/`OVER`/
 * `LATE_DELIVERY`) — `DAMAGED`/`WRONG_*` เป็นงานของเคส NCR (D-108) ไม่ใช่ของหน้านี้
 *
 * อนุมัติไม่ได้อยู่ในกลุ่มนี้: `POST /v1/receiving-discrepancies/{id}/approve`
 * เป็นเส้นเดิมของ D-091 ที่มี grant ของตัวเอง (`INVENTORY` + `APPROVE` ไม่ใช่
 * `PROCUREMENT` + `VIEW` ที่เปิดหน้าจอนี้) ชนิดข้อมูลของผลอนุมัติจึงพอขั้นต่ำ
 * (`ApprovedReceivingDiscrepancy`) ไม่ใช่ทั้ง `ReceivingDiscrepancyResponse` —
 * สิ่งที่หน้าจอต้องใช้ต่อแก้แถวในคิวมีแค่สถานะใหม่กับผู้อนุมัติ/เวลา
 */

/* ------------------------------------------------------------ ชนิด/ค่าคงที่ -- */

/**
 * สามชนิดที่คิวนี้แสดง — ตรงกับ `QUEUE_TYPES` ฝั่งเซิร์ฟเวอร์
 * (`modules/inventory/receiving_exceptions.py`)
 *
 * ใช้เป็น union ของพารามิเตอร์ตัวกรองเท่านั้น (คนละเรื่องกับฟิลด์ `discrepancy_type`
 * ในแถวซึ่งอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง — ดูเหตุผลที่ `parseReceivingExceptionItem`)
 */
export type ReceivingExceptionTypeFilter = 'SHORT' | 'OVER' | 'LATE_DELIVERY';

/** แหล่งที่มาของแถว — คนที่กรอกใน ERP เอง หรือระบบภายนอก (D-168) */
export type ReceivingExceptionSourceFilter = 'ERP_DESK' | 'INTEGRATION';

/** ช่วงวันที่ไม่บังคับของ `summary` เกินได้สูงสุดกี่วัน — ตรงกับ `MAX_SUMMARY_DAYS` */
export const SUMMARY_MAX_RANGE_DAYS = 366;

/** ค่าเริ่มต้นของตัวกรองช่วงวันที่ในส่วนรายงานสรุป (invariant ของ WO นี้) */
export const SUMMARY_DEFAULT_RANGE_DAYS = 90;

/**
 * สี่สถานะของ `receiving_discrepancy.status` (D-091 `DiscrepancyStatus`) —
 * ประกาศซ้ำไว้ในโดเมนนี้แทนการ import ข้าม backend/frontend ด้วยเหตุผลเดียวกับ
 * `INVENTORY_MODULE` ใน `lib/quality/types.ts`: enum ฝั่งเซิร์ฟเวอร์ไม่ถูก export
 * ออกมาให้ web ใช้ ค่าทั้งสี่นี้คือสตริงที่ CHECK constraint ยอมรับ ไม่ใช่ union ที่
 * parser กรองด้วย (ดูเหตุผลที่ `ReceivingExceptionItem.status`)
 */
export const DISCREPANCY_STATUS_OPEN = 'OPEN';
export const DISCREPANCY_STATUS_PENDING_MANAGEMENT_APPROVAL = 'PENDING_MANAGEMENT_APPROVAL';
export const DISCREPANCY_STATUS_APPROVED = 'APPROVED';
export const DISCREPANCY_STATUS_REJECTED = 'REJECTED';

/* ----------------------------------------------------------------- กฎธุรกิจ -- */

/** `422` — ช่วงวันที่ของ `summary` เกิน 366 วัน */
export const RECEIVING_EXCEPTIONS_RANGE_TOO_WIDE = 'RECEIVING_EXCEPTIONS_RANGE_TOO_WIDE';

/** `422` — `date_to` มาก่อน `date_from` */
export const RECEIVING_EXCEPTIONS_RANGE_IS_REVERSED = 'RECEIVING_EXCEPTIONS_RANGE_IS_REVERSED';

/**
 * `422` ของ `approve` — ผู้อนุมัติเป็นผู้บันทึกรายการนี้เอง (flow ข้อ 4.6, D-010)
 *
 * **ไม่ใช่เรื่องสิทธิ์ไม่พอ** — ผู้กดถือ `INVENTORY` + `APPROVE` ครบแล้ว แต่เป็นผู้
 * บันทึกเอง การแบ่งแยกหน้าที่จึงให้คนอื่นเป็นผู้อนุมัติ
 */
export const APPROVER_MUST_DIFFER_FROM_RAISER = 'APPROVER_MUST_DIFFER_FROM_RAISER';

/**
 * `422` ของ `approve` — ผู้กดคือคนที่เปิด PO เดิมกลับหลัง short-close (D-168 ผู้ใช้
 * ตัดสิน Q-SOD) คนละเรื่องกับ `APPROVER_MUST_DIFFER_FROM_RAISER`: ที่นี่ไม่ใช่ผู้บันทึก
 * discrepancy แต่เป็นผู้ที่แก้ไข PO ของรอบนั้นเอง จึงอนุมัติ SHORT ที่เกิดจากรอบนั้น
 * ไม่ได้ (คนละคู่ตา)
 */
export const SOD_REOPENER_CANNOT_APPROVE = 'SOD_REOPENER_CANNOT_APPROVE';

/** `409` ของ `approve` — แถวไม่อยู่ใน `OPEN`/`PENDING_MANAGEMENT_APPROVAL` อีกแล้ว */
export const RECEIVING_DISCREPANCY_NOT_APPROVABLE = 'RECEIVING_DISCREPANCY_NOT_APPROVABLE';

/* -------------------------------------------------------------- resource --- */

/**
 * หนึ่งแถวในคิว — สิบเก้าฟิลด์ตามที่ `invariants` ของ WO นี้ประกาศไว้ตรง ๆ
 *
 * `discrepancy_type`/`status`/`source` อ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union
 * ของค่าที่หน้าจอรู้จัก ด้วยเหตุผลเดียวกับทุก parser อื่นในแอปนี้: CHECK constraint
 * ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่หน้าจอยังไม่มีคำอธิบายให้ทิ้ง
 * จะทำให้ **แถวที่มีอยู่จริงหายไปจากคิว** ซึ่งแย่กว่าการแสดงป้ายที่ยังไม่มีคำอธิบาย
 */
export interface ReceivingExceptionItem {
  readonly discrepancy_id: string;
  readonly plant_code: string;
  readonly po_id: string;
  readonly po_number: string;
  readonly po_line_no: number;
  readonly supplier_code: string;
  readonly supplier_name: string;
  readonly product_code: string;
  readonly product_name: string;
  readonly discrepancy_type: string;
  readonly status: string;
  readonly expected_qty: string;
  readonly actual_qty: string;
  /** `null` สำหรับ `LATE_DELIVERY` ซึ่งไม่มีมูลค่า (ไม่มีอะไรโพสต์) */
  readonly value: string | null;
  readonly source: string;
  readonly reason_code: string | null;
  readonly reason: string | null;
  /** คำของ Store เอง ไม่ใช่ identity ใน ERP เลย (D-168) */
  readonly reported_by_ref: string | null;
  /** `true` = short-close ที่ทำให้เกิดแถวนี้ถูกเปิด PO กลับแล้ว (D-168 A6) */
  readonly closure_reopened: boolean;
  readonly raised_by_employee_id: string | null;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly created_at: string;
}

/** `GET /v1/receiving-exceptions` — envelope มาตรฐานของทุกกลุ่ม */
export interface ReceivingExceptionListPage {
  readonly items: readonly ReceivingExceptionItem[];
  readonly next_cursor: string | null;
}

/** พารามิเตอร์ของ `GET /v1/receiving-exceptions` ที่ contract รับ */
export interface ReceivingExceptionListQuery {
  readonly plant_code?: string;
  /** ค่าเริ่มต้นฝั่งเซิร์ฟเวอร์คือ `false` — ไม่ส่งแปลว่าขอค่าเริ่มต้นนั้น */
  readonly include_closed?: boolean;
  readonly discrepancy_type?: ReceivingExceptionTypeFilter;
  readonly supplier_code?: string;
  readonly source?: ReceivingExceptionSourceFilter;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/** หนึ่งแถวของรายงานสรุป — พับตาม (เดือน, โรงงาน, ผู้ขาย, ชนิด, เหตุผล) */
export interface ReceivingExceptionSummaryRow {
  readonly month: string;
  readonly plant_code: string;
  readonly supplier_code: string;
  readonly supplier_name: string;
  readonly discrepancy_type: string;
  readonly reason_code: string | null;
  readonly count: number;
  readonly value: string;
}

/** ยอดรวมห้าค่าของรายงานสรุป */
export interface ReceivingExceptionSummaryTotals {
  readonly exceptions: number;
  readonly short_value: string;
  readonly over_value: string;
  readonly late_deliveries: number;
  readonly orders_closed_short: number;
}

/** `GET /v1/receiving-exceptions/summary` */
export interface ReceivingExceptionSummary {
  readonly date_from: string;
  readonly date_to: string;
  readonly rows: readonly ReceivingExceptionSummaryRow[];
  readonly totals: ReceivingExceptionSummaryTotals;
}

/** พารามิเตอร์ของ `GET /v1/receiving-exceptions/summary` — `date_from`/`date_to` บังคับ */
export interface ReceivingExceptionSummaryQuery {
  readonly date_from: string;
  readonly date_to: string;
  readonly plant_code?: string;
  readonly supplier_code?: string;
}

/**
 * ผลของ `POST /v1/receiving-discrepancies/{id}/approve` — **เฉพาะสี่ฟิลด์ที่หน้าจอนี้
 * ใช้ต่อ** ไม่ใช่ `ReceivingDiscrepancyResponse` เต็มรูป (ซึ่งมีฟิลด์ของ D-091 ที่คิวนี้
 * ไม่แสดงอยู่แล้ว เช่น `receipt_id`) — แก้แถวในหน่วยความจำด้วยค่าที่เซิร์ฟเวอร์ตอบมา
 * จึงไม่ต้องโหลดทั้งตารางใหม่ (invariant ของ WO นี้)
 */
export interface ApprovedReceivingDiscrepancy {
  readonly discrepancy_id: string;
  readonly status: string;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
}
